import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';
import { Events } from '../events/events.provider';
import { validate as validateUuid } from 'uuid';

/**
 * Storage Provider
 *
 * Ce service permet de gérer la partie storage de l'application via Ionic Storage
 */
@Injectable({
  providedIn: 'root',
})
export class StorageProvider {
  /**
   * Logger
   */
  private logger: LoggerWrapper;

  /**
   * Constructeur
   *
   * Crée une instance de StorageProvider.
   *
   * @param {Storage} storage
   * @param loggerFactory
   * @param events
   */
  constructor(
    private storage: Storage,
    private loggerFactory: LoggerFactory,
    private events: Events
  ) {
    this.logger = this.loggerFactory.getInstance(this);
  }

  /**
   * Vérifie si une clé existe dans le storage
   *
   * @param {string} key clé de storage
   */

  async exist(key: string): Promise<Boolean> {
    return await this.storage.get(key).then((v) => {
      return v != null;
    });
  }

  // Get the value associated with the given key.
  /**
   * Get d'une valeur de storage a partir de sa clé
   *
   * @param {string} key clé de storage
   * @param {boolean} each_doc récupération de chaque document associé si c'est un tableau
   */
  async get(key: string, each_doc = true) {
    let data = await this.storage.get(key);

    if (!data) console.warn(`No data found in storage for key ${key} and each_doc ${each_doc}`);
    if (data != null && data.length > 0 && each_doc == true) {
      let docs = [];
      for (let id of data) {
        let result = await this.storage.get(id);
        if (result != null) docs.push(result);
      }

      return docs;
    } else {
      return data;
    }
  }

  // Set the value for the given key.
  /**
   * Enregistrement d'une valeur de storage a partir de sa clé et de sa valeur
   *
   * @param {string} key clé de storage
   * @param {string} value valeur
   * @param {boolean} each_doc enregistrement de chaque document associé si c'est un tableau
   */
  async set(key: string, value: any, each_doc = true) {
    // this.logger.log("set cache", key, JSON.stringify(value));
    // this.logger.log("value : " + value);
    if (value != null && value.length > 0 && each_doc == true) {
      let docs = [];
      for (let doc of value) {
        docs.push(doc.id);
        await this.storage.set(doc.id, doc).then(() => {});
      }
      return this.storage.set(key, docs);
    } else {
      return this.storage.set(key, value);
    }
  }

  //Remove any value associated with this key.
  /**
   * Supression d'une clé de storage
   *
   * @param {string} key clé de storage
   */
  remove(key: string) {
    return this.storage.remove(key);
  }

  /**
   * Ajout d'un brouillon
   *
   * @param draft
   * @param list_name
   */
  async addDraft(draft: any, list_name: string | null = null) {
    // this.logger.log("addDraft " + list_name, draft);
    if (list_name && list_name == 'DraftOtUnplanned') {
      this.events.publish('cr_list_update');
    }

    let draft_id = draft['id'];
    if (list_name != null) draft_id = list_name + '_' + draft['id'];

    // Ajout du brouillon
    await this.storage.set(draft_id, draft).then(() => {});

    // On s'arrete là si le brouillon ne fait pas partie d'une liste
    if (list_name == null) return;

    // Ajout du brouillon dans la liste
    let data = await this.storage.get(list_name);
    if (data != null && data.length > 0) {
      // La liste existe déjà
      // this.logger.log('La liste existe déjà', data);
      if (data.indexOf(draft_id) === -1) {
        // this.logger.log('On ajoute a la liste');
        data.push(draft_id);
        return this.storage.set(list_name, data);
      } else {
        // this.logger.log('On ne change rien dans la liste');
      }
    } else {
      // On crée la liste
      // this.logger.log('on crée la liste et on ajoute a la liste');
      data = [];
      data.push(draft_id);
      return this.storage.set(list_name, data);
    }
  }

  /**
   * Supression d'un brouillon
   *
   * @param {string} storage_name clé de la requete
   * @param {string} data resultat d'une requete
   */
  async removeDraft(draft: any, list_name = null) {
    // this.logger.log("remove " + list_name, draft);
    if (list_name && list_name == 'DraftOtUnplanned') {
      this.events.publish('cr_list_update');
    }
    //Error chrono waiting in cr-synthese
    if (draft === null) {
      return;
    }

    let draft_id = draft['id'];
    if (list_name != null) draft_id = list_name + '_' + draft['id'];

    // this.logger.log('remove draft:', draft_id);
    // On supprime le brouillon
    await this.remove(draft_id);

    // On s'arrete là si le brouillon ne fait pas partie d'une liste
    if (list_name == null) return;

    let data = await this.storage.get(list_name);
    if (data != null && data.length > 0) {
      var index = data.indexOf(draft_id);
      if (index > -1) {
        data.splice(index, 1);
        return this.storage.set(list_name, data);
      }
    }
    return false;
  }

  // Transform url and params to storage key
  /**
   * Génération d'une clé de storage à partir de l'API, l'url et les paramètres
   *
   * @param {string} url url de la requete
   * @param {string} params parametres de la requete
   */
  getStorageName(url: string, params = null) {
    if (params != null) {
      url =
        url +
        Object.keys(params)
          .map(function (k) {
            return encodeURIComponent(k) + '=' + encodeURIComponent(params[k]);
          })
          .join('&');
    }
    var queryName, queryUrl, queryStr, queryArr, queryObj;
    queryName = url.slice(url.indexOf('//') + 2, url.indexOf('.'));

    if (url.indexOf('?') == -1) {
      queryUrl = url.slice(url.indexOf('api') + 4, url.length + 1).replace(/[/]/g, '_');
      // this.logger.log("queryName : " + queryName);
      // this.logger.log("queryUrl : " + queryUrl);
      // console.log("getStorageName B", queryName + '_' + queryUrl);
      return queryName + '_' + queryUrl;
    } else {
      queryUrl = url.slice(url.indexOf('api') + 4, url.indexOf('?')).replace(/[/]/g, '_');
      queryStr = url.slice(url.indexOf('?') + 1);
      queryArr = queryStr.split('&');
      queryObj = '';

      for (var i = 0; i < queryArr.length; i++) {
        if (i == 0) queryObj = queryArr[i].split('=')[0] + '_' + queryArr[i].split('=')[1];
        else queryObj = queryObj + '_' + queryArr[i].split('=')[0] + '_' + queryArr[i].split('=')[1];
      }
      // console.log("getStorageName B", queryName + '_' + queryUrl + '_' + queryObj);
      return queryName + '_' + queryUrl + '_' + queryObj;
    }
  }

  /**
   * Supression des fichiers en local qui ont été supprimé en remote
   *
   * @param {string} storage_name clé de la requete
   * @param {string} data resultat d'une requete
   */
  removeDeletedFiles(storage_name: string, data: any[]) {
    let results: any[] = [];

    return new Promise((resolve) => {
      this.get(storage_name, false)
        .then((storage) => {
          if (storage != null) {
            // On transforme le resultat en ne gardant que les doc.id
            for (let elem of data) {
              results.push(elem['id']);
            }
            // On supprime les elements qui ont été supprimés
            for (let elem of storage) {
              if (results.indexOf(elem) == -1) this.remove(elem);
            }
          }
          resolve(null);
        })
        .catch((err) => {
          this.logger.warn(err);
          resolve(null);
        });
    });
  }

  async removeExistingGetInitData() {
    this.logger.debug('Deleting existing get init data');
    await this.storage.forEach((_: any, key: string) => {
      const isDactechBackUrl = key.includes('dactech_back');
      if (validateUuid(key) || isDactechBackUrl) {
        this.storage.remove(key).then();
      }
    });
    this.logger.debug('Existing get init data deleted');
  }
}
